// Copied from original/src/lib/theme.ts (chat C30, O-10, D-285). localStorage persistence replaced by the
// personal preference `ui.theme_studio` (themeFromPreference / themeForPreference below); the rest is unchanged.
export interface CustomAppTheme {
  id: string;
  name: string;
  nameAr: string;
  primary: string; // e.g. #0274c7
  primaryHover: string;
  primaryLight: string;
  primaryTint: string;
  primaryDark: string;
  accent: string;
  palette?: string[];
  imageUrl?: string;
  isCustomImage?: boolean;
}

export const PRESET_THEMES: CustomAppTheme[] = [
  {
    id: 'sunset_sage_glass',
    name: 'Sunset Coral & Sage Glass',
    nameAr: 'الهوية البصرية العصرية (الزجاجي والمشرق)',
    primary: '#f97342',
    primaryHover: '#ea580c',
    primaryLight: '#fb923c',
    primaryTint: '#fff7ed',
    primaryDark: '#9a3412',
    accent: '#2dd4bf',
    palette: ['#f97342', '#2dd4bf', '#facc15', '#38bdf8', '#121619']
  },
  {
    id: 'emerald_glass',
    name: 'Emerald Glassmorphism',
    nameAr: 'الزمرد الزجاجي الحديث',
    primary: '#10b981',
    primaryHover: '#059669',
    primaryLight: '#34d399',
    primaryTint: '#ecfdf5',
    primaryDark: '#064e3b',
    accent: '#65d337',
    palette: ['#10b981', '#34d399', '#65d337', '#064e3b', '#ecfdf5']
  },
  {
    id: 'azure_classic',
    name: 'Classic Azure',
    nameAr: 'الأزرق الفيلسوفي الكلاسيكي',
    primary: '#0274c7',
    primaryHover: '#025fa3',
    primaryLight: '#38aef7',
    primaryTint: '#f0f7ff',
    primaryDark: '#082a4a',
    accent: '#0e91e9',
    palette: ['#0274c7', '#0e91e9', '#38aef7', '#082a4a', '#f0f7ff']
  },
  {
    id: 'emerald_royal',
    name: 'Royal Emerald',
    nameAr: 'الزمرد الملكي',
    primary: '#059669',
    primaryHover: '#047857',
    primaryLight: '#34d399',
    primaryTint: '#ecfdf5',
    primaryDark: '#064e3b',
    accent: '#10b981',
    palette: ['#059669', '#10b981', '#34d399', '#064e3b', '#ecfdf5']
  },
  {
    id: 'violet_imperial',
    name: 'Imperial Violet',
    nameAr: 'البنفسجي الإمبراطوري',
    primary: '#7c3aed',
    primaryHover: '#6d28d9',
    primaryLight: '#c084fc',
    primaryTint: '#f3e8ff',
    primaryDark: '#4c1d95',
    accent: '#a855f7',
    palette: ['#7c3aed', '#a855f7', '#c084fc', '#4c1d95', '#f3e8ff']
  },
  {
    id: 'sunset_gold',
    name: 'Sunset Gold',
    nameAr: 'الذهب المشرق',
    primary: '#d97706',
    primaryHover: '#b45309',
    primaryLight: '#fbbf24',
    primaryTint: '#fffbeb',
    primaryDark: '#78350f',
    accent: '#f59e0b',
    palette: ['#d97706', '#f59e0b', '#fbbf24', '#78350f', '#fffbeb']
  },
  {
    id: 'cyber_teal',
    name: 'Cyber Teal',
    nameAr: 'السايبر تيل العصري',
    primary: '#0d9488',
    primaryHover: '#0f766e',
    primaryLight: '#2dd4bf',
    primaryTint: '#f0fdfa',
    primaryDark: '#134e4a',
    accent: '#14b8a6',
    palette: ['#0d9488', '#14b8a6', '#2dd4bf', '#134e4a', '#f0fdfa']
  },
  {
    id: 'crimson_ruby',
    name: 'Crimson Ruby',
    nameAr: 'العنابي الياقوتي',
    primary: '#e11d48',
    primaryHover: '#be123c',
    primaryLight: '#fb7185',
    primaryTint: '#fff1f2',
    primaryDark: '#881337',
    accent: '#f43f5e',
    palette: ['#e11d48', '#f43f5e', '#fb7185', '#881337', '#fff1f2']
  },
  {
    id: 'slate_obsidian',
    name: 'Slate Obsidian',
    nameAr: 'السليت الفحمي',
    primary: '#334155',
    primaryHover: '#1e293b',
    primaryLight: '#64748b',
    primaryTint: '#f8fafc',
    primaryDark: '#0f172a',
    accent: '#475569',
    palette: ['#334155', '#475569', '#64748b', '#0f172a', '#f8fafc']
  }
];

export const STORAGE_KEY_THEME = 'alfaylasuf_active_theme';

export function hexToRgbTuple(hex: string): string {
  try {
    let clean = hex.replace('#', '').trim();
    if (clean.length === 3) {
      clean = clean[0]! + clean[0]! + clean[1]! + clean[1]! + clean[2]! + clean[2]!;
    }
    const r = parseInt(clean.substring(0, 2), 16) || 0;
    const g = parseInt(clean.substring(2, 4), 16) || 0;
    const b = parseInt(clean.substring(4, 6), 16) || 0;
    return `${r}, ${g}, ${b}`;
  } catch {
    return '2, 116, 199';
  }
}

export function applyThemeToDocument(theme: CustomAppTheme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // Apply primary color tokens
  root.style.setProperty('--color-primary-500', theme.primary);
  root.style.setProperty('--color-primary-600', theme.primaryHover || theme.primary);
  root.style.setProperty('--color-primary-400', theme.primaryLight || theme.primary);
  root.style.setProperty('--color-primary-50', theme.primaryTint || '#f0f7ff');
  root.style.setProperty('--color-primary-900', theme.primaryDark || '#082a4a');
  root.style.setProperty('--accent-color', theme.accent || theme.primaryLight || '#f59e0b');

  // RGB values for opacity/glow helpers
  const primaryRgb = hexToRgbTuple(theme.primary);
  const accentRgb = hexToRgbTuple(theme.accent || theme.primary);
  root.style.setProperty('--primary-rgb', primaryRgb);
  root.style.setProperty('--accent-rgb', accentRgb);
}

/**
 * [C30] The old loadSavedTheme() rule applied to the stored preference `ui.theme_studio` (D-123) instead of
 * localStorage: a theme without a primary colour, or the retired "azure_classic" (#0274c7), falls back to the first
 * preset (original/src/lib/theme.ts:171-184).
 */
export function themeFromPreference(value: unknown): CustomAppTheme {
  const v = value as { [K in keyof CustomAppTheme]?: unknown } | null | undefined;
  const hex = (x: unknown): x is string => typeof x === 'string' && /^#[0-9a-fA-F]{6}$/.test(x);
  const str = (x: unknown, max: number): x is string => typeof x === 'string' && x.length <= max;
  // [C30 review] every field checked before it reaches style.setProperty or the modal: anything malformed → preset 0
  if (
    v && typeof v === 'object' && str(v.id, 64) && str(v.name, 120) && str(v.nameAr, 120) &&
    hex(v.primary) && hex(v.primaryHover) && hex(v.primaryLight) && hex(v.primaryTint) && hex(v.primaryDark) && hex(v.accent) &&
    (v.palette === undefined || (Array.isArray(v.palette) && v.palette.length <= 12 && v.palette.every(hex))) &&
    (v.isCustomImage === undefined || typeof v.isCustomImage === 'boolean') &&
    v.id !== 'azure_classic' && v.primary !== '#0274c7'
  ) {
    return {
      id: v.id, name: v.name, nameAr: v.nameAr, primary: v.primary, primaryHover: v.primaryHover, primaryLight: v.primaryLight,
      primaryTint: v.primaryTint, primaryDark: v.primaryDark, accent: v.accent,
      ...(v.palette ? { palette: v.palette as string[] } : {}), ...(v.isCustomImage ? { isCustomImage: true } : {}),
    };
  }
  return PRESET_THEMES[0]!;
}

/** The stored form: the extracted image itself (a data: URL) is not kept, only its colours. */
export function themeForPreference(theme: CustomAppTheme): Record<string, unknown> {
  const { imageUrl: _image, ...rest } = theme;
  return { ...rest };
}

export function extractColorsFromImageFile(file: File): Promise<CustomAppTheme> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context unavailable'));
          return;
        }

        // Downscale image for quick pixel sampling
        const width = 120;
        const height = Math.max(1, Math.round((img.height / img.width) * 120));
        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);
        const imageData = ctx.getImageData(0, 0, width, height).data;

        // Bucket colors into RGB bins
        const colorCounts: Record<string, { r: number; g: number; b: number; count: number; sat: number; brightness: number }> = {};

        for (let i = 0; i < imageData.length; i += 4) {
          const r = imageData[i]!;
          const g = imageData[i + 1]!;
          const b = imageData[i + 2]!;
          const a = imageData[i + 3]!;

          // Ignore transparent pixels
          if (a < 128) continue;

          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const brightness = (r + g + b) / 3;

          // Filter pure white and pure black
          if (brightness > 248 || brightness < 12) continue;

          // Quantize RGB to 16-step grid
          const qr = Math.round(r / 16) * 16;
          const qg = Math.round(g / 16) * 16;
          const qb = Math.round(b / 16) * 16;
          const key = `${qr},${qg},${qb}`;

          const sat = max === 0 ? 0 : (max - min) / max;

          if (!colorCounts[key]) {
            colorCounts[key] = { r: qr, g: qg, b: qb, count: 0, sat, brightness };
          }
          colorCounts[key].count += 1;
        }

        const sortedColors = Object.values(colorCounts).sort((a, b) => {
          // Score by saturation and frequency
          const scoreA = a.count * (1 + a.sat * 2.5);
          const scoreB = b.count * (1 + b.sat * 2.5);
          return scoreB - scoreA;
        });

        const primaryRgb = sortedColors[0] || { r: 14, g: 145, b: 233, sat: 0.8, brightness: 120 };
        
        // Find distinct accent color
        const accentCandidate = sortedColors.find(c => {
          const dist = Math.abs(c.r - primaryRgb.r) + Math.abs(c.g - primaryRgb.g) + Math.abs(c.b - primaryRgb.b);
          return dist > 90;
        });
        const accentRgb = accentCandidate || { 
          r: Math.min(255, primaryRgb.r + 40), 
          g: Math.min(255, primaryRgb.g + 50), 
          b: Math.min(255, primaryRgb.b + 60) 
        };

        const rgbToHex = (r: number, g: number, b: number) => 
          '#' + [r, g, b].map(x => Math.min(255, Math.max(0, Math.round(x))).toString(16).padStart(2, '0')).join('');

        const primaryHex = rgbToHex(primaryRgb.r, primaryRgb.g, primaryRgb.b);
        const primaryHoverHex = rgbToHex(primaryRgb.r * 0.82, primaryRgb.g * 0.82, primaryRgb.b * 0.82);
        const primaryLightHex = rgbToHex(
          Math.min(255, primaryRgb.r + (255 - primaryRgb.r) * 0.45),
          Math.min(255, primaryRgb.g + (255 - primaryRgb.g) * 0.45),
          Math.min(255, primaryRgb.b + (255 - primaryRgb.b) * 0.45)
        );
        const primaryTintHex = rgbToHex(
          Math.min(255, primaryRgb.r + (255 - primaryRgb.r) * 0.90),
          Math.min(255, primaryRgb.g + (255 - primaryRgb.g) * 0.90),
          Math.min(255, primaryRgb.b + (255 - primaryRgb.b) * 0.90)
        );
        const primaryDarkHex = rgbToHex(primaryRgb.r * 0.4, primaryRgb.g * 0.4, primaryRgb.b * 0.4);
        const accentHex = rgbToHex(accentRgb.r, accentRgb.g, accentRgb.b);

        const topPalette = sortedColors.slice(0, 5).map(c => rgbToHex(c.r, c.g, c.b));

        const customTheme: CustomAppTheme = {
          id: `custom_img_${Date.now()}`,
          name: 'Custom Image Theme',
          nameAr: 'ثيم مخصص من صورة',
          primary: primaryHex,
          primaryHover: primaryHoverHex,
          primaryLight: primaryLightHex,
          primaryTint: primaryTintHex,
          primaryDark: primaryDarkHex,
          accent: accentHex,
          palette: topPalette,
          imageUrl: event.target?.result as string,
          isCustomImage: true
        };

        resolve(customTheme);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = event.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}
